import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Plus, Calendar, Filter } from 'lucide-react';
import saasApiService from '../../../services/saasApi';
import RevenueForm from './RevenueForm';
import ExpenseForm from './ExpenseForm';

interface FinanceSummary {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  monthlyRevenue: number;
  monthlyExpenses: number;
  monthlyProfit: number;
}

interface Revenue {
  id: number;
  client_id: number;
  client_name?: string;
  amount: number;
  description: string;
  service_date: string;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue';
  invoice_number: string;
}

interface Expense {
  id: number;
  vehicle_id: number;
  vehicle_info?: string;
  amount: number;
  description: string;
  expense_type: 'fuel' | 'maintenance' | 'insurance' | 'permits' | 'outsourcing' | 'other';
  expense_date: string;
  receipt_document?: string;
}

const FinanceManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'summary' | 'revenues' | 'expenses'>('summary');
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRevenueForm, setShowRevenueForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  
  const [selectedRevenue, setSelectedRevenue] = useState<Revenue | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [revenueToMark, setRevenueToMark] = useState<Revenue | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [libsReady, setLibsReady] = useState<{ xlsx: boolean; jspdf: boolean; exceljs: boolean }>({ xlsx: false, jspdf: false, exceljs: false });
  // Selección de ingresos para exportación
  const [selectedRevenueIds, setSelectedRevenueIds] = useState<Set<number>>(new Set());
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const allSelected = revenues.length > 0 && selectedRevenueIds.size === revenues.length;
  const anySelected = selectedRevenueIds.size > 0;
  const toggleSelectAllRevenues = () => {
    if (allSelected) setSelectedRevenueIds(new Set());
    else setSelectedRevenueIds(new Set(revenues.map(r => r.id)));
  };
  const toggleSelectRevenue = (id: number) => {
    setSelectedRevenueIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetchFinanceData();
  }, []);

  // Load export libraries on demand
  useEffect(() => {
    const ensureScript = (src: string) => new Promise<void>((resolve) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => resolve();
      document.head.appendChild(s);
    });
    (async () => {
      await ensureScript('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
      await ensureScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
      await ensureScript('https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.0/dist/jspdf.plugin.autotable.min.js');
      await ensureScript('https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js');
      setLibsReady({
        xlsx: !!(window as any).XLSX,
        jspdf: !!((window as any).jspdf || (window as any).jsPDF),
        exceljs: !!(window as any).ExcelJS
      });
    })();
  }, []);

  const fetchFinanceData = async () => {
    try {
      setLoading(true);
      const [summaryRes, revenuesRes, expensesRes, clientsRes, vehiclesRes] = await Promise.all([
        saasApiService.getFinancialSummary(),
        saasApiService.getRevenues(),
        saasApiService.getExpenses(),
        saasApiService.getClients?.().catch(() => []),
        saasApiService.getVehicles?.().catch(() => [])
      ]);

      console.log('Finance data received:', { summaryRes, revenuesRes, expensesRes });
      
      // Procesar resumen financiero
      const processedSummary = {
        totalRevenue: summaryRes?.totals?.total_revenue || 0,
        totalExpenses: summaryRes?.totals?.total_expenses || 0,
        netProfit: summaryRes?.totals?.net_profit || 0,
        monthlyRevenue: summaryRes?.totals?.total_revenue || 0, // Por ahora usar el total
        monthlyExpenses: summaryRes?.totals?.total_expenses || 0,
        monthlyProfit: summaryRes?.totals?.net_profit || 0
      };

      // Build lookup maps for client name and vehicle plate
      const clientMap: Record<string | number, string> = {};
      (clientsRes || []).forEach((c: any) => {
        const id = c?.id ?? c?.client_id;
        const name = c?.name || c?.company_name || c?.razon_social || '';
        if (id != null && name) clientMap[id] = name;
      });
      const vehicleMap: Record<string | number, string> = {};
      (vehiclesRes || []).forEach((v: any) => {
        const id = v?.id ?? v?.vehicle_id ?? v?.tractor_id;
        const plate = v?.plate || v?.license_plate || v?.placa || '';
        if (id != null && plate) vehicleMap[id] = plate;
      });

      const enrichedRevenues = (revenuesRes || []).map((r: any) => {
        const clientId = r?.client_id ?? r?.cliente_id;
        const vehicleId = r?.vehicle_id ?? r?.tractor_id ?? r?.truck_id;
        const client_name = r?.client_name || r?.client?.name || r?.client?.company_name || (clientId != null ? clientMap[clientId] : '') || '';
        const vehicle_plate = r?.vehicle_plate || r?.tractor_plate || r?.truck_plate || r?.vehicle?.plate || r?.tractor?.plate || (vehicleId != null ? vehicleMap[vehicleId] : '') || '';
        return { ...r, client_name, vehicle_plate, tractor_plate: r?.tractor_plate || vehicle_plate };
      });

      setSummary(processedSummary);
      setRevenues(enrichedRevenues);
      setExpenses(expensesRes || []);
      } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const statusEs = (status: string) => {
    switch (status) {
      case 'paid': return 'Pagado';
      case 'pending': return 'Pendiente';
      case 'overdue': return 'Vencido';
      default: return status;
    }
  };

  const formatDateEs = (d: string | Date) => new Date(d).toLocaleDateString('es-PE');

  // Helper: load image as data URL from public path
  const loadImageDataUrl = async (path: string): Promise<string | null> => {
    try {
      const res = await fetch(path);
      const blob = await res.blob();
      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
      });
    } catch (_) { return null; }
  };

  const exportRevenuesPDF = async () => {
    const title = '';
    const headers = ['Tracto','Fecha','Cliente','Num. Guía Remisión','Num. Guía Remitente','Origen','Destino','Pago'];
    const get = (obj: any, keys: string[], fallback: any = '') => {
      for (const k of keys) {
        if (obj && obj[k] != null && obj[k] !== '') return obj[k];
      }
      return fallback;
    };
    const rows = revenues.map(r => {
      const tracto = get(r, ['tractor','tracto','vehicle_info','vehicle_plate','tractor_plate','truck_plate'], '');
      const fecha = formatDateEs(r.service_date);
      const cliente = r.client_name || '';
      const guiaClt = get(r, [
        'remission_guide_number',
        'remision_guide_number',
        'num_guia_clt',
        'remision_number',
        'guia_remision',
        'guiaRemision',
        'remisionNumber',
        'num_guia_remision',
        'numero_guia_remision',
        'guia_de_remision',
        'guia_cliente',
        'guia_clt',
        'guiaRemisionCliente',
        'nro_guia_remision'
      ], '');
      const guiaRem = get(r, ['sender_guide_number','num_guia_remitente','remitente_number','guia_remitente'], '');
      const origen = get(r, ['origin','origen'], '');
      const destino = get(r, ['destination','destino'], '');
      const pago = formatCurrency(Number(r.amount || 0));
      return [tracto, fecha, cliente, guiaClt, guiaRem, origen, destino, pago];
    });

    if (libsReady.jspdf) {
      const jspdfNS = (window as any).jspdf || (window as any);
      const jsPDF = jspdfNS.jsPDF || jspdfNS.JSPDF || jspdfNS.jsPDF;
      // Landscape to fit all columns nicely
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
      // Header with logo and company info
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginLeft = 40;
      const marginTop = 36;
      let cursorY = marginTop;
      try {
        const logo = await loadImageDataUrl('/logopdf.png');
        if (logo) {
          doc.addImage(logo, 'PNG', marginLeft, cursorY, 170, 58);
        }
      } catch {}
      doc.setFontSize(12);
      doc.setTextColor(80);
      const companyLines = [
        'RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.',
        'RUC: 20611798572'
      ];
      companyLines.forEach((line, idx) => {
        doc.text(line, pageWidth - 40 - doc.getTextWidth(line), cursorY + 14 + idx * 14);
      });
      cursorY += 54;
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text(title, marginLeft, cursorY);
      cursorY += 14;
      const autoTable = (doc as any).autoTable;
      if (autoTable) {
        (doc as any).autoTable({
          head: [headers],
          body: rows,
          startY: cursorY + 6,
          styles: { fontSize: 9, cellPadding: 6, overflow: 'linebreak' },
          headStyles: { fillColor: [33, 150, 243], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 70 },  // Tracto
            1: { cellWidth: 70 },  // Fecha
            2: { cellWidth: 120 }, // Cliente
            3: { cellWidth: 110 }, // Num. Guía Remisión
            4: { cellWidth: 120 }, // Num. Guía Remitente
            5: { cellWidth: 110 }, // Origen
            6: { cellWidth: 130 }, // Destino
            7: { cellWidth: 70, halign: 'right' } // Pago
          },
          didDrawPage: (data: any) => {
            doc.setFontSize(10);
            doc.setTextColor(120);
            const pageH = doc.internal.pageSize.getHeight();
            const bank0 = 'Número de cuenta Interbank Royal Trucking';
            const bank1 = 'Número de cuenta: 2003007170210';
            const bank2 = 'CCI: 003-200-003007170210-31';
            const generated = `Generado: ${new Date().toLocaleString('es-PE')}`;
            doc.text(bank0, marginLeft, pageH - 42);
            doc.text(bank1, marginLeft, pageH - 28);
            doc.text(bank2, marginLeft, pageH - 14);
            doc.text(generated, pageWidth - 40 - doc.getTextWidth(generated), pageH - 14);
          }
        });
      }
      doc.save('ingresos.pdf');
      return;
    }
    // Fallback
    exportDataset(title, headers, rows, 'pdf');
  };

  // Export helper (component scope)
  const exportDataset = (title: string, headers: string[], rows: any[][], format: 'pdf' | 'excel') => {
    const fileBase = title.replace(/\s+/g, '_').toLowerCase();
    if (format === 'excel') {
      if (libsReady.xlsx && (window as any).XLSX) {
        const XLSX = (window as any).XLSX;
        const data = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Datos');
        XLSX.writeFile(wb, `${fileBase}.xlsx`);
      } else {
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${fileBase}.csv`; a.click(); URL.revokeObjectURL(url);
      }
      return;
    }
    if (libsReady.jspdf) {
      const jspdfNS = (window as any).jspdf || (window as any);
      const jsPDF = jspdfNS.jsPDF || jspdfNS.JSPDF || jspdfNS.jsPDF;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.text(title, 40, 40);
      const autoTable = (doc as any).autoTable;
      if (autoTable) {
        (doc as any).autoTable({ head: [headers], body: rows.map(r => r.map(c => String(c))), startY: 60, styles: { fontSize: 10 } });
      }
      doc.save(`${fileBase}.pdf`);
      return;
    }
    window.print();
  };

  // Exportar solo los ingresos seleccionados (PDF en landscape)
  const exportSelectedRevenuesPDF = async () => {
    const subset = revenues.filter((r: any) => selectedRevenueIds.has(r.id));
    if (subset.length === 0) return;
    const title = '';
    const headers = ['Tracto','Fecha','Cliente','Num. Guía Remisión','Num. Guía Remitente','Origen','Destino','Pago'];
    const get = (obj: any, keys: string[], fallback: any = '') => {
      for (const k of keys) { if (obj && obj[k] != null && obj[k] !== '') return obj[k]; }
      return fallback;
    };
    const rows = subset.map((r: any) => {
      const tracto = get(r, ['vehicle_plate','tractor_plate','truck_plate','tractor','tracto','vehicle_info'], '') || r?.tractor?.plate || r?.vehicle?.plate || r?.truck?.plate || '';
      const fecha = formatDateEs(r.service_date);
      const cliente = r?.client_name || r?.client?.name || r?.client?.company_name || '';
      const guiaClt = get(r, ['remission_guide_number','remision_guide_number','num_guia_clt','remision_number','guia_remision','guiaRemision','remisionNumber','num_guia_remision','numero_guia_remision','guia_de_remision','guia_cliente','guia_clt','guiaRemisionCliente','nro_guia_remision'], '');
      const guiaRem = get(r, ['sender_guide_number','num_guia_remitente','remitente_number','guia_remitente'], '');
      const origen = get(r, ['origin','origen'], '');
      const destino = get(r, ['destination','destino'], '');
      const pago = formatCurrency(Number(r.amount || 0));
      return [tracto, fecha, cliente, guiaClt, guiaRem, origen, destino, pago];
    });

    if (libsReady.jspdf) {
      const jspdfNS = (window as any).jspdf || (window as any);
      const jsPDF = jspdfNS.jsPDF || jspdfNS.JSPDF || jspdfNS.jsPDF;
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginLeft = 40;
      const marginTop = 36;
      let cursorY = marginTop;
      try {
        const logo = await loadImageDataUrl('/logopdf.png');
        if (logo) doc.addImage(logo, 'PNG', marginLeft, cursorY, 170, 58);
      } catch {}
      doc.setFontSize(12); doc.setTextColor(80);
      const companyLines = ['RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.','RUC: 20611798572'];
      companyLines.forEach((line, idx) => doc.text(line, pageWidth - 40 - doc.getTextWidth(line), cursorY + 14 + idx * 14));
      cursorY += 54;
      if (title) { doc.setFontSize(16); doc.setTextColor(0); doc.text(title, marginLeft, cursorY); cursorY += 14; }
      const autoTable = (doc as any).autoTable;
      if (autoTable) {
        (doc as any).autoTable({
          head: [headers], body: rows, startY: cursorY + 6,
          margin: { left: marginLeft, right: 40 },
          tableWidth: 'auto',
          styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
          headStyles: { fillColor: [33,150,243], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 68 },
            1: { cellWidth: 68 },
            2: { cellWidth: 120 },
            3: { cellWidth: 110 },
            4: { cellWidth: 120 },
            5: { cellWidth: 110 },
            6: { cellWidth: 130 },
            7: { cellWidth: 70, halign: 'right' }
          },
          didDrawPage: () => {
            doc.setFontSize(10); doc.setTextColor(120);
            const pageH = doc.internal.pageSize.getHeight();
            const bank0 = 'Número de cuenta Interbank Royal Trucking';
            const bank1 = 'Número de cuenta: 2003007170210';
            const bank2 = 'CCI: 003-200-003007170210-31';
            const generated = `Generado: ${new Date().toLocaleString('es-PE')}`;
            doc.text(bank0, marginLeft, pageH - 42);
            doc.text(bank1, marginLeft, pageH - 28);
            doc.text(bank2, marginLeft, pageH - 14);
            doc.text(generated, pageWidth - 40 - doc.getTextWidth(generated), pageH - 14);
          }
        });
      }
      doc.save('ingresos_seleccion.pdf');
      return;
    }
    exportDataset('Ingresos (Selección)', headers, rows, 'pdf');
  };

  // Exportar Excel mejorado para Ingresos (con encabezado empresa, formatos y totales)
  const exportRevenuesExcel = async () => {
    // Columns requested: Tracto, Fecha, Cliente (nombre), Num. Guía Remisión, Num. Guía Remitente, Origen, Destino, Pago
    const headers = ['Tracto','Fecha','Cliente','Num. Guía Remisión','Num. Guía Remitente','Origen','Destino','Pago'];
    // Helper to pick possible field names safely
    const get = (obj: any, keys: string[], fallback: any = '') => {
      for (const k of keys) {
        if (obj && obj[k] != null && obj[k] !== '') return obj[k];
      }
      return fallback;
    };
    const rows = revenues.map(r => {
      const tracto = get(r, ['tractor','tracto','vehicle_info','vehicle_plate','tractor_plate','truck_plate'], '')
        || get(r as any, ['tractor_plate'])
        || (r as any)?.tractor?.plate
        || (r as any)?.vehicle?.plate
        || (r as any)?.truck?.plate
        || '';
      const fecha = formatDateEs(r.service_date);
      const cliente = (r as any).client_name || (r as any)?.client?.name || (r as any)?.client?.company_name || '';
      const guiaClt = get(r, [
        'remision_guide_number',
        'num_guia_clt',
        'remision_number',
        'guia_remision',
        'guiaRemision',
        'remisionNumber',
        'num_guia_remision',
        'numero_guia_remision',
        'guia_de_remision',
        'guia_cliente',
        'guia_clt',
        'guiaRemisionCliente',
        'nro_guia_remision'
      ], '');
      const guiaRem = get(r, ['sender_guide_number','num_guia_remitente','remitente_number','guia_remitente'], '');
      const origen = get(r, ['origin','origen'], '');
      const destino = get(r, ['destination','destino'], '');
      const pago = Number(r.amount || 0);
      return [tracto, fecha, cliente, guiaClt, guiaRem, origen, destino, pago];
    });
    // Preferred: ExcelJS styled workbook (bordes, rellenos, zebra, merges, logo)
    if ((window as any).ExcelJS && libsReady.exceljs) {
      const ExcelJS = (window as any).ExcelJS;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Ingresos');

      // Column widths adapted to desired layout
      ws.columns = [
        { header: headers[0], key: 'tracto', width: 12 },
        { header: headers[1], key: 'fecha', width: 14 },
        { header: headers[2], key: 'cliente', width: 28 },
        { header: headers[3], key: 'guia_clt', width: 18 },
        { header: headers[4], key: 'guia_rem', width: 20 },
        { header: headers[5], key: 'origen', width: 22 },
        { header: headers[6], key: 'destino', width: 26 },
        { header: headers[7], key: 'pago', width: 14, style: { numFmt: '[$S/] #,##0.00' } }
      ];

      // Company header (merge across A..H and align right), then centered title
      ws.addRow(['RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.']); // row 1
      ws.addRow(['RUC: 20611798572']); // row 2
      ws.addRow([' ']); // row 3 spacer
      ws.addRow(['Ingresos']); // row 4 title
      ws.mergeCells(1, 1, 1, 8); // A1:H1
      ws.mergeCells(2, 1, 2, 8); // A2:H2
      ws.mergeCells(4, 1, 4, 8); // A4:H4
      ws.getCell(1, 1).alignment = { horizontal: 'right' };
      ws.getCell(2, 1).alignment = { horizontal: 'right' };
      const titleCell = ws.getCell(4, 1);
      titleCell.font = { size: 16, bold: true };
      titleCell.alignment = { horizontal: 'center' };

      // Header style (blue fill, white font, borders)
      const headerRow = ws.addRow(headers); // typically row 5
      headerRow.eachCell((cell: any) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90CAF9' } }; // lighter blue
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });

      // Freeze header
      ws.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRow.number }];

      // Data rows with zebra and borders
      rows.forEach((r, idx) => {
        const row = ws.addRow(r);
        const isAlt = idx % 2 === 0;
        row.eachCell((cell: any, colNumber: number) => {
          cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
          };
          if (isAlt) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F4F7' } }; // subtle zebra
          }
          if (colNumber === 8) {
            cell.numFmt = '[$S/] #,##0.00';
            cell.alignment = { horizontal: 'right' };
          }
          if ([3,6,7].includes(colNumber)) {
            cell.alignment = { horizontal: 'left', wrapText: true };
          }
        });
        // Improve row height slightly for wrapped text
        row.height = 18;
      });

      // Totals (sum on Pago column H)
      const totalRow = ws.addRow(['', '', '', '', '', '', 'Total', { formula: `SUM(H${headerRow.number + 1}:H${ws.rowCount})` }]);
      totalRow.eachCell((cell: any, col: number) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        if (col === 7) cell.font = { bold: true };
        if (col === 8) cell.numFmt = '[$S/] #,##0.00';
      });
      totalRow.eachCell((cell: any) => {
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      });

      // Bank info
      ws.addRow([]);
      ws.addRow(['Número de cuenta Interbank Royal Trucking']);
      ws.addRow(['Número de cuenta: 2003007170210']);
      ws.addRow(['CCI: 003-200-003007170210-31']);

      // Optional logo at top-left (A1) if available (slightly larger)
      try {
        const logoDataUrl = await loadImageDataUrl('/logopdf.png');
        if (logoDataUrl) {
          const base64 = logoDataUrl.split(',')[1];
          const imageId = wb.addImage({ base64, extension: 'png' });
          ws.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 190, height: 65 }
          });
        }
      } catch {}

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'ingresos.xlsx'; a.click(); URL.revokeObjectURL(url);
      return;
    }
    if ((window as any).XLSX && libsReady.xlsx) {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();
      const title = 'Ingresos';
      const companyRows = [
        ['RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.'],
        ['RUC: 20611798572'],
        [''],
        [title],
        ['']
      ];
      const data: any[][] = [
        ...companyRows,
        headers,
        ...rows
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      // Ancho de columnas
      (ws as any)['!cols'] = [
        { wch: 14 }, // Fecha
        { wch: 28 }, // Cliente
        { wch: 60 }, // Descripción
        { wch: 12 }, // Estado
        { wch: 14 }, // Monto
        { wch: 14 }, // Vencimiento
        { wch: 18 }  // Factura
      ];
      // Unir celdas del título (A4:G4 -> índice base 0 -> fila 3)
      (ws as any)['!merges'] = (ws as any)['!merges'] || [];
      (ws as any)['!merges'].push({ s: { r: 3, c: 0 }, e: { r: 3, c: 6 } });
      // Formato moneda en columna E (índice 4)
      const startRow = companyRows.length + 1; // fila de headers
      const firstDataRow = startRow + 1; // primera fila de datos
      const lastDataRow = firstDataRow + rows.length - 1;
      for (let r = firstDataRow; r <= lastDataRow; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: 4 });
        if ((ws as any)[addr]) (ws as any)[addr].z = '[$S/] #,##0.00';
      }
      // Fila de totales
      const totalRowIdx = lastDataRow + 1;
      (ws as any)[XLSX.utils.encode_cell({ r: totalRowIdx, c: 3 })] = { t: 's', v: 'Total' };
      const sumFrom = XLSX.utils.encode_cell({ r: firstDataRow, c: 4 });
      const sumTo = XLSX.utils.encode_cell({ r: lastDataRow, c: 4 });
      (ws as any)[XLSX.utils.encode_cell({ r: totalRowIdx, c: 4 })] = { t: 'n', f: `SUM(${sumFrom}:${sumTo})`, z: '[$S/] #,##0.00' } as any;
      // Datos bancarios al final
      (ws as any)[XLSX.utils.encode_cell({ r: totalRowIdx + 2, c: 0 })] = { t: 's', v: 'Número de cuenta: 2003007170210' };
      (ws as any)[XLSX.utils.encode_cell({ r: totalRowIdx + 3, c: 0 })] = { t: 's', v: 'CCI: 003-200-003007170210-31' };

      XLSX.utils.book_append_sheet(wb, ws, 'Ingresos');
      XLSX.writeFile(wb, 'ingresos.xlsx');
      return;
    }
    // Fallback CSV
    exportDataset('Ingresos', headers, rows.map(r => [r[0], r[1], r[2], r[3], String(r[4]), r[5], r[6]]), 'excel');
  };

  // Exportar Excel con estilo para Gastos (componente)
  const exportExpensesExcel = async () => {
    const headers = ['Fecha','Vehículo','Tipo','Descripción','Monto'];
    const typeEs = (t: string) => (
      t === 'fuel' ? 'Combustible' :
      t === 'maintenance' ? 'Mantenimiento' :
      t === 'insurance' ? 'Seguro' :
      t === 'permits' ? 'Permisos' :
      t === 'outsourcing' ? 'Tercerización' : 'Otro'
    );
    const rows = expenses.map(e => [
      formatDateEs(e.expense_date),
      e.vehicle_info || e.vehicle_id,
      typeEs(e.expense_type),
      e.description || '',
      Number(e.amount)
    ]);
    if ((window as any).ExcelJS && libsReady.exceljs) {
      const ExcelJS = (window as any).ExcelJS;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Gastos');

      ws.columns = [
        { header: headers[0], key: 'fecha', width: 16 },
        { header: headers[1], key: 'veh', width: 28 },
        { header: headers[2], key: 'tipo', width: 18 },
        { header: headers[3], key: 'desc', width: 60 },
        { header: headers[4], key: 'monto', width: 14, style: { numFmt: '[$S/] #,##0.00' } },
      ];

      ws.addRow(['RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.']);
      ws.addRow(['RUC: 20611798572']);
      ws.addRow([' ']);
      ws.addRow(['Gastos']);
      ws.mergeCells(4, 1, 4, 5);
      ws.getCell(4, 1).font = { size: 16, bold: true };

      const headerRow = ws.addRow(headers);
      headerRow.eachCell((cell: any) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2196F3' } };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });

      rows.forEach((r, idx) => {
        const row = ws.addRow(r);
        const isAlt = idx % 2 === 0;
        row.eachCell((cell: any, colNumber: number) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          if (isAlt) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
          if (colNumber === 5) { cell.numFmt = '[$S/] #,##0.00'; cell.alignment = { horizontal: 'right' }; }
        });
      });

      const totalRow = ws.addRow(['', '', '', 'Total', { formula: `SUM(E${headerRow.number + 1}:E${ws.rowCount})` }]);
      totalRow.getCell(4).font = { bold: true };
      totalRow.eachCell((cell: any) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      ws.addRow([]);
      ws.addRow(['Número de cuenta: 2003007170210']);
      ws.addRow(['CCI: 003-200-003007170210-31']);

      try {
        const logoDataUrl = await loadImageDataUrl('/logopdf.png');
        if (logoDataUrl) {
          const base64 = logoDataUrl.split(',')[1];
          const imageId = wb.addImage({ base64, extension: 'png' });
          ws.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 140, height: 48 } });
        }
      } catch {}

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'gastos.xlsx'; a.click(); URL.revokeObjectURL(url);
      return;
    }
    // Fallback: XLSX simple o CSV
    if ((window as any).XLSX && libsReady.xlsx) {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();
      const data: any[][] = [
        ['RAZÓN SOCIAL: ROYAL TRUKING E.I.R.L.'],
        ['RUC: 20611798572'],
        [''],
        ['Gastos'],
        [''],
        headers,
        ...rows
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      (ws as any)['!cols'] = [ { wch: 16 }, { wch: 28 }, { wch: 18 }, { wch: 60 }, { wch: 14 } ];
      XLSX.utils.book_append_sheet(wb, ws, 'Gastos');
      XLSX.writeFile(wb, 'gastos.xlsx');
      return;
    }
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gastos.csv'; a.click(); URL.revokeObjectURL(url);
  };

  // Outsourcing feature removed

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExpenseTypeColor = (type: string) => {
    switch (type) {
      case 'fuel': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-orange-100 text-orange-800';
      case 'insurance': return 'bg-purple-100 text-purple-800';
      case 'permits': return 'bg-indigo-100 text-indigo-800';
      case 'outsourcing': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Gestión Financiera</h1>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowRevenueForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Ingreso
          </button>
          <button 
            onClick={() => setShowExpenseForm(true)}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Gasto
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'summary', name: 'Resumen', icon: DollarSign },
            { id: 'revenues', name: 'Ingresos', icon: TrendingUp },
            { id: 'expenses', name: 'Gastos', icon: TrendingDown }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Ingresos Totales</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary?.totalRevenue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Gastos Totales</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary?.totalExpenses || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Ganancia Neta</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(summary?.netProfit || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Summary */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Resumen del Mes</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-500">Ingresos del Mes</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(summary?.monthlyRevenue || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Gastos del Mes</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(summary?.monthlyExpenses || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Ganancia del Mes</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(summary?.monthlyProfit || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outsourcing Tab removed */}

      {/* Revenues Tab */}
      {activeTab === 'revenues' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Ingresos</h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowRevenueForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Ingreso
              </button>
              <button
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onClick={exportRevenuesExcel}
                title="Exportar Excel"
              >
                Exportar Excel
              </button>
              <button
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onClick={exportRevenuesPDF}
                title="Exportar PDF"
              >
                Exportar PDF
              </button>
              {selectMode && (
                <>
                  <button
                    className={`px-3 py-2 rounded-md text-sm ${anySelected ? 'border border-gray-300' : 'border border-gray-200 text-gray-400 cursor-not-allowed'}`}
                    onClick={exportSelectedRevenuesPDF}
                    disabled={!anySelected}
                    title="Exportar PDF (selección)"
                  >
                    Exportar PDF (selección)
                  </button>
                  <button
                    className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm"
                    onClick={toggleSelectAllRevenues}
                    title={allSelected ? 'Quitar selección' : 'Seleccionar todos'}
                  >
                    {allSelected ? 'Quitar selección' : 'Seleccionar todos'}
                  </button>
                </>
              )}
              <button
                className={`px-3 py-2 rounded-md text-sm ${selectMode ? 'border border-blue-300 text-blue-700' : 'border border-gray-300'}`}
                onClick={() => { setSelectMode((v) => { const nv = !v; if (!nv) setSelectedRevenueIds(new Set()); return nv; }); }}
                title={selectMode ? 'Salir de selección' : 'Seleccionar elementos'}
              >
                {selectMode ? 'Salir de selección' : 'Seleccionar'}
              </button>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm">
                <Filter className="h-4 w-4 mr-2" />
                Filtrar
              </button>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {revenues.map((revenue) => (
                <li key={revenue.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {selectMode && (
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                          checked={selectedRevenueIds.has(revenue.id)}
                          onChange={() => toggleSelectRevenue(revenue.id)}
                          aria-label={`Seleccionar ingreso ${revenue.id}`}
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {revenue.description}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(revenue.status)}`}>
                            {revenue.status === 'paid' ? 'Pagado' : revenue.status === 'pending' ? 'Pendiente' : 'Vencido'}
                          </span>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              Servicio: {new Date(revenue.service_date).toLocaleDateString()}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              Vencimiento: {new Date(revenue.due_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center space-x-3 sm:mt-0">
                            <p className="text-lg font-bold text-green-600">
                              {formatCurrency(revenue.amount)}
                            </p>
                            {revenue.status === 'pending' && (
                              <button
                                onClick={() => {
                                  setRevenueToMark(revenue);
                                  setPaymentMethod('cash');
                                  setPaymentDate(new Date().toISOString().split('T')[0]);
                                  setShowMarkPaid(true);
                                }}
                                className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                              >
                                Marcar Pagado
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (!window.confirm('¿Eliminar este ingreso? Esta acción no se puede deshacer.')) return;
                                try {
                                  await saasApiService.deleteRevenue(revenue.id);
                                  await fetchFinanceData();
                                } catch (e) {
                                  console.error('Error eliminando ingreso', e);
                                  alert('No se pudo eliminar el ingreso');
                                }
                              }}
                              className="px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 border"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Gastos</h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowExpenseForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Gasto
              </button>
              <button
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onClick={exportExpensesExcel}
                title="Exportar Excel"
              >
                Exportar Excel
              </button>
              <button
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onClick={() => exportDataset('Gastos', ['Fecha','Vehículo','Tipo','Descripción','Monto'], expenses.map(e => [
                  new Date(e.expense_date).toLocaleDateString(), e.vehicle_info || e.vehicle_id, e.expense_type, e.description, e.amount
                ]), 'pdf')}
                title="Exportar PDF"
              >
                Exportar PDF
              </button>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm">
                <Filter className="h-4 w-4 mr-2" />
                Filtrar
              </button>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {expenses.map((expense) => (
                <li key={expense.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {expense.description}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExpenseTypeColor(expense.expense_type)}`}>
                          {expense.expense_type === 'fuel' ? 'Combustible' : 
                           expense.expense_type === 'maintenance' ? 'Mantenimiento' :
                           expense.expense_type === 'insurance' ? 'Seguro' :
                           expense.expense_type === 'permits' ? 'Permisos' : 'Otro'}
                        </span>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {new Date(expense.expense_date).toLocaleDateString()}
                          </p>
                          {expense.vehicle_info && (
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              Vehículo: {expense.vehicle_info}
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <p className="text-lg font-bold text-red-600">
                            {formatCurrency(expense.amount)}
                          </p>
                          <button
                            onClick={async () => {
                              if (!window.confirm('¿Eliminar este gasto? Esta acción no se puede deshacer.')) return;
                              try {
                                await saasApiService.deleteExpense(expense.id);
                                await fetchFinanceData();
                              } catch (e) {
                                console.error('Error eliminando gasto', e);
                                alert('No se pudo eliminar el gasto');
                              }
                            }}
                            className="ml-3 px-3 py-1.5 text-sm rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 border"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Revenue Form Modal */}
      <RevenueForm
        isOpen={showRevenueForm}
        onClose={() => {
          setShowRevenueForm(false);
          setSelectedRevenue(null);
        }}
        onSave={() => {
          fetchFinanceData();
          setShowRevenueForm(false);
          setSelectedRevenue(null);
        }}
        revenue={selectedRevenue}
      />

      {/* Outsourcing Modal removed */}

      {/* Expense Form Modal */}
      <ExpenseForm
        isOpen={showExpenseForm}
        onClose={() => {
          setShowExpenseForm(false);
          setSelectedExpense(null);
        }}
        onSave={() => {
          fetchFinanceData();
          setShowExpenseForm(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
      />

      {/* Mark as Paid Modal */}
      {showMarkPaid && revenueToMark && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Marcar como Pagado</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Fecha de Pago</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Método de Pago</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="cash">Efectivo</option>
                  <option value="transfer">Transferencia</option>
                  <option value="check">Cheque</option>
                  <option value="card">Tarjeta</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300"
                onClick={() => {
                  setShowMarkPaid(false);
                  setRevenueToMark(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                onClick={async () => {
                  try {
                    await saasApiService.markRevenuePaid(revenueToMark.id, {
                      payment_date: paymentDate,
                      payment_method: paymentMethod,
                    });
                    await fetchFinanceData();
                    setShowMarkPaid(false);
                    setRevenueToMark(null);
                  } catch (err) {
                    console.error('Error marking revenue as paid:', err);
                    alert('No se pudo marcar como pagado');
                  }
                }}
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceManager;
